import React from 'react'
import PropTypes from 'prop-types'

/**
 * Small checkmark SVG used inside AnsweredStrip badges.
 */
export const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path
      d="M2 5l2.5 2.5 3.5-4"
      stroke="white"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * Displays a previously-answered question as a compact strip.
 * Clicking anywhere on the strip (or pressing Enter) calls onEdit,
 * which clears the field so the user can change their answer.
 */
export const AnsweredStrip = ({ questionLabel, answerLabel, answerType = 'other', onEdit }) => (
  <div
    className="elig-answered"
    onClick={onEdit}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => e.key === 'Enter' && onEdit()}
  >
    <div className="ea-check">
      <CheckIcon />
    </div>
    <span className="ea-q">{questionLabel}</span>
    <span className={`ea-val ${answerType}`}>{answerLabel}</span>
    <span className="ea-edit">✎ edit</span>
  </div>
)

AnsweredStrip.propTypes = {
  questionLabel: PropTypes.string.isRequired,
  answerLabel: PropTypes.string.isRequired,
  /** CSS modifier appended to ea-val: 'yes', 'no', or 'other' */
  answerType: PropTypes.string,
  onEdit: PropTypes.func.isRequired,
}

/**
 * Card wrapper for an unanswered eligibility question.
 * meta is shown as a small label above the question body.
 */
export const QuestionCard = ({ meta, children, style }) => (
  <div className="q-card" style={style}>
    {meta && <div className="q-meta">{meta}</div>}
    {children}
  </div>
)

QuestionCard.propTypes = {
  meta: PropTypes.string,
  children: PropTypes.node.isRequired,
  style: PropTypes.object,
}

/**
 * Yes / No two-button grid used for binary eligibility questions.
 * Pass selectedValue to highlight the active choice.
 */
export const YNGrid = ({
  onYes,
  onNo,
  yesLabel = 'Yes',
  noLabel = 'No',
  yesSub,
  noSub,
  selectedValue,
}) => (
  <div className="yn-grid">
    <button
      type="button"
      className={`yn-btn${selectedValue === 'yes' ? ' sel-yes' : ''}`}
      onClick={onYes}
    >
      <div className="yn-icon">✓</div>
      <div className="yn-label">{yesLabel}</div>
      {yesSub && <div className="yn-sub">{yesSub}</div>}
    </button>
    <button
      type="button"
      className={`yn-btn${selectedValue === 'no' ? ' sel-no' : ''}`}
      onClick={onNo}
    >
      <div className="yn-icon">✗</div>
      <div className="yn-label">{noLabel}</div>
      {noSub && <div className="yn-sub">{noSub}</div>}
    </button>
  </div>
)

YNGrid.propTypes = {
  onYes: PropTypes.func.isRequired,
  onNo: PropTypes.func.isRequired,
  yesLabel: PropTypes.string,
  noLabel: PropTypes.string,
  yesSub: PropTypes.string,
  noSub: PropTypes.string,
  selectedValue: PropTypes.string,
}
